import { safeValidateWorkoutTemplate } from '../../validation/template.schemas';
import { getFieldsForWorkoutExerciseTrackingType } from '../measurement';
import { isSetValueValid } from './template-form';

import type { SetField } from '../../types/domain/workout.types';
import type { DifficultyLevel } from '../../validation/common.schemas';
import type {
  TemplateExerciseOutput,
  TemplateSetOutput,
  WorkoutTemplateOutput,
} from '../../validation/template.schemas';

/**
 * Workout Form Model
 *
 * The standalone workout form's state shape, its exercise operations and its
 * validation, as pure functions — shared by the web hook (`useWorkoutForm`) and
 * the mobile form so both edit a workout by the same rules. Nothing here knows
 * about React, storage or the network.
 *
 * Group operations (superset / tri-set / giant set) live beside this in
 * `utils/exercise-groups`.
 */

// =============================================================================
// STATE
// =============================================================================

/** The fields the workout form edits. */
export interface WorkoutFormState {
  name: string;
  description: string;
  difficulty_level: DifficultyLevel;
  rest_duration: number;
  exercises: TemplateExerciseOutput[];
}

/** The form's defaults — an intermediate workout resting 60 seconds. */
export const WORKOUT_FORM_DEFAULTS: WorkoutFormState = {
  name: '',
  description: '',
  difficulty_level: 'intermediate',
  rest_duration: 60,
  exercises: [],
};

/** The rest-between-sets stepper: 10–300 seconds in steps of 10. */
export const REST_STEPPER = {
  min: 10,
  max: 300,
  step: 10,
  unit: 'sec',
} as const;

/** A set's value fields, in the order a set row shows them. */
const SET_FIELDS: readonly SetField[] = [
  'weight',
  'reps',
  'duration',
  'distance',
];

/**
 * A set as the form holds it. The server sends some values as decimal text
 * (a duration of `"88.00"`), where the form and the schema work in numbers.
 */
function toFormSet(set: TemplateSetOutput): TemplateSetOutput {
  const next = { ...set };
  for (const field of SET_FIELDS) {
    const value: unknown = next[field];
    if (typeof value !== 'string') continue;

    const parsed = Number(value);
    next[field] =
      value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
  }
  return next;
}

/**
 * An exercise as the form holds it: its sets' values as numbers (see
 * {@link toFormSet}), and no null group type. The server sends
 * `group_type: null` for an exercise in no group, where the schema expects the
 * field to be absent — left as it came, every saved workout with an ungrouped
 * exercise would fail validation the moment it was edited.
 */
export function toFormExercise(
  exercise: TemplateExerciseOutput
): TemplateExerciseOutput {
  const { group_type: groupType, ...rest } = exercise;
  return {
    ...rest,
    ...(groupType === null ? {} : { group_type: groupType }),
    sets: exercise.sets?.map(toFormSet) ?? exercise.sets,
  };
}

/** The form's starting state: a draft or a saved workout over the defaults. */
export function createWorkoutFormState(
  initial?: Partial<WorkoutTemplateOutput>
): WorkoutFormState {
  return {
    name: initial?.name ?? WORKOUT_FORM_DEFAULTS.name,
    description: initial?.description ?? WORKOUT_FORM_DEFAULTS.description,
    difficulty_level:
      initial?.difficulty_level ?? WORKOUT_FORM_DEFAULTS.difficulty_level,
    rest_duration:
      initial?.rest_duration ?? WORKOUT_FORM_DEFAULTS.rest_duration,
    exercises:
      initial?.exercises?.map(toFormExercise) ??
      WORKOUT_FORM_DEFAULTS.exercises,
  };
}

/** The payload the form saves — and the shape a draft is stored in. */
export function toWorkoutTemplate(
  state: WorkoutFormState
): WorkoutTemplateOutput {
  return {
    name: state.name,
    description: state.description,
    difficulty_level: state.difficulty_level,
    rest_duration: state.rest_duration,
    exercises: state.exercises,
  };
}

// =============================================================================
// EXERCISE OPERATIONS
// =============================================================================

/** Appends exercises to the end of the list. */
export function addExercises(
  exercises: TemplateExerciseOutput[],
  added: TemplateExerciseOutput[]
): TemplateExerciseOutput[] {
  return [...exercises, ...added];
}

/** Removes one exercise. */
export function removeExercise(
  exercises: TemplateExerciseOutput[],
  exerciseUuid: string
): TemplateExerciseOutput[] {
  return exercises.filter((e) => e.uuid !== exerciseUuid);
}

/** Replaces one exercise's sets. */
export function updateExerciseSets(
  exercises: TemplateExerciseOutput[],
  exerciseUuid: string,
  sets: TemplateSetOutput[]
): TemplateExerciseOutput[] {
  return exercises.map((e) => (e.uuid === exerciseUuid ? { ...e, sets } : e));
}

/**
 * Puts the exercises in the given order. A uuid that matches no exercise is
 * skipped, and an exercise left out of the order is dropped.
 */
export function reorderExercises(
  exercises: TemplateExerciseOutput[],
  exerciseUuids: string[]
): TemplateExerciseOutput[] {
  return exerciseUuids
    .map((uuid) => exercises.find((e) => e.uuid === uuid))
    .filter((e): e is TemplateExerciseOutput => e !== undefined);
}

// =============================================================================
// VALIDATION
// =============================================================================

/** One message per top-level field — what a form shows under each. */
export interface WorkoutValidationErrors {
  name?: string;
  description?: string;
  difficulty_level?: string;
  rest_duration?: string;
  exercises?: string;
}

/** An exercise holding sets that cannot be saved as they are. */
export interface WorkoutExerciseProblem {
  exerciseUuid: string;
  exerciseName: string;
  /** One-based numbers of the sets with a value missing or not valid. */
  setNumbers: number[];
  /** Which values are wrong, across those sets. */
  fields: SetField[];
}

export interface WorkoutFormValidation {
  valid: boolean;
  errors: WorkoutValidationErrors;
  /** The exercises behind `errors.exercises`, for marking them in a list. */
  exerciseProblems: WorkoutExerciseProblem[];
}

/**
 * What to tell the user when sets block the save: each exercise by name, in
 * quotes — exercise names contain "and" and commas themselves — with the
 * values it is missing.
 */
export function describeExerciseProblems(
  problems: WorkoutExerciseProblem[]
): string {
  const list = problems
    .map((p) => `“${p.exerciseName}” (${p.fields.join(', ')})`)
    .join(', ');
  const lead =
    problems.length === 1
      ? 'This exercise has incomplete sets'
      : 'These exercises have incomplete sets';
  return `${lead}: ${list}.`;
}

/**
 * Gathers what is wrong with a list of exercises' sets, fault by fault, and
 * reports it per exercise. The workout form and the plan form (once per
 * workout) both check their sets through it.
 */
export interface ExerciseFaultCollector {
  /** A set of this exercise is at fault; `field` says which value, if known. */
  add: (exerciseUuid: string, setNumber: number, field?: SetField) => void;
  /**
   * Checks every set for each value its exercise's tracking type calls for —
   * the check the schema cannot make.
   */
  addMissingValues: (exercises: TemplateExerciseOutput[]) => void;
  /** The exercises with a fault, in list order. */
  toProblems: (exercises: TemplateExerciseOutput[]) => WorkoutExerciseProblem[];
}

export function createExerciseFaultCollector(): ExerciseFaultCollector {
  // What is wrong, per exercise: set number → the fields at fault.
  const faults = new Map<string, Map<number, Set<SetField>>>();

  const add: ExerciseFaultCollector['add'] = (uuid, setNumber, field) => {
    const sets = faults.get(uuid) ?? new Map<number, Set<SetField>>();
    const fields = sets.get(setNumber) ?? new Set<SetField>();
    if (field) fields.add(field);
    sets.set(setNumber, fields);
    faults.set(uuid, sets);
  };

  return {
    add,
    addMissingValues: (exercises): void => {
      for (const exercise of exercises) {
        const required = getFieldsForWorkoutExerciseTrackingType(
          exercise.tracking_type
        );
        (exercise.sets ?? []).forEach((set, index) => {
          for (const field of required) {
            if (!isSetValueValid(field, set[field])) {
              add(exercise.uuid, index + 1, field);
            }
          }
        });
      }
    },
    toProblems: (exercises) =>
      exercises
        .filter((exercise) => faults.has(exercise.uuid))
        .map((exercise) => {
          const sets =
            faults.get(exercise.uuid) ?? new Map<number, Set<SetField>>();
          const atFault = new Set([...sets.values()].flatMap((f) => [...f]));
          return {
            exerciseUuid: exercise.uuid,
            exerciseName: exercise.name,
            setNumbers: [...sets.keys()].sort((x, y) => x - y),
            fields: SET_FIELDS.filter((f) => atFault.has(f)),
          };
        }),
  };
}

/** The set field an issue's path names, if it names one. */
export function toSetField(value: unknown): SetField | undefined {
  return SET_FIELDS.find((field) => field === value);
}

/**
 * Validates the form: the shared workout schema, then every set against its
 * exercise's tracking type.
 *
 * Field errors are the schema's own messages, the last issue per field winning.
 * Sets are the exception, twice over. The schema's message for a bad value
 * names neither the exercise nor the set ("Number must be greater than or equal
 * to 1"), and the schema does not know which values a set needs: a weighted,
 * timed exercise with no weight passes it. So a set is checked here for each
 * value its tracking type calls for (`isSetValueValid`), and what is wrong is
 * gathered per exercise and described in a sentence.
 */
export function validateWorkoutForm(
  state: WorkoutFormState
): WorkoutFormValidation {
  const result = safeValidateWorkoutTemplate(toWorkoutTemplate(state));

  const errors: WorkoutValidationErrors = {};
  const faults = createExerciseFaultCollector();

  for (const issue of result.success ? [] : result.error.issues) {
    const [field, exerciseIndex, part, setIndex, setField] = issue.path;
    if (typeof field !== 'string') continue;

    const exercise =
      field === 'exercises' && typeof exerciseIndex === 'number'
        ? state.exercises[exerciseIndex]
        : undefined;

    if (exercise && part === 'sets' && typeof setIndex === 'number') {
      faults.add(exercise.uuid, setIndex + 1, toSetField(setField));
      continue;
    }

    errors[field as keyof WorkoutValidationErrors] = issue.message;
  }

  faults.addMissingValues(state.exercises);
  const exerciseProblems = faults.toProblems(state.exercises);

  if (exerciseProblems.length > 0) {
    errors.exercises = describeExerciseProblems(exerciseProblems);
  }

  return {
    valid: result.success && exerciseProblems.length === 0,
    errors,
    exerciseProblems,
  };
}

/** Whether the form can be saved as it stands. */
export function isWorkoutFormValid(state: WorkoutFormState): boolean {
  return validateWorkoutForm(state).valid;
}
